// "1 Duft" / "3er-Bundle" choice on the product page (snippets/bundle-offer.liquid).
//
// `mode` is what the shopper picked, `active-mode` what applies to the selected
// size: a size without a bundle falls back to "single" but keeps the pick, so
// switching back to a size with a bundle restores it. The CSS hides the regular
// buy flow while active-mode is "bundle".
//
// With a discount code in the cart, the bundle cards show the bundle parent's
// discounted price (same co2 logic as price-preview.js and bundle.js).
class BundleOffer extends HTMLElement {
    connectedCallback() {
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.addEventListener('change', (event) => {
            if (event.target.type !== 'radio') return;
            this.setAttribute('mode', event.target.value);
            this.update();
        }, { signal });

        // Matched on the form element: the theme's detail.formId is form.id, which
        // the form's <input name="id"> shadows, so it is that input, not the ID.
        document.addEventListener('variant:change', (event) => {
            if (event.target.getAttribute('id') !== this.getAttribute('form') || !event.detail.variant) return;
            this.showVariant(event.detail.variant.id);
        }, { signal });

        // Adding, replacing or removing the code in the cart drawer
        ['cart:refresh', 'cart:change'].forEach((eventName) => {
            document.addEventListener(eventName, () => this.loadCodeDiscount(), { signal });
        });

        this.update();
        this.loadCodeDiscount();
    }

    disconnectedCallback() {
        this.abortController.abort();
    }

    showVariant(variantId) {
        this.querySelectorAll('.bundle-offer__group').forEach((group) => {
            group.hidden = group.dataset.variantId !== String(variantId);
        });
        this.update();
    }

    update() {
        const group = this.querySelector('.bundle-offer__group:not([hidden])');
        const hasBundle = group && group.dataset.hasBundle === 'true';
        const mode = hasBundle ? this.getAttribute('mode') : 'single';

        this.setAttribute('active-mode', mode);
        this.hidden = !hasBundle;
        this.querySelectorAll('.bundle-offer__card input[type="radio"]').forEach((radio) => {
            radio.checked = radio.value === mode;
        });
    }

    async loadCodeDiscount(tries = 0) {
        if (typeof co2 === 'undefined') {
            if (tries < 60) setTimeout(() => this.loadCodeDiscount(tries + 1), 100);
            return;
        }

        const cards = this.querySelectorAll('.bundle-offer__card[data-mode="bundle"]');
        try {
            const cart = await fetch(window.Shopify.routes.root + 'cart.js').then((response) => response.json());
            const codes = cart.discount_codes || [];
            if (codes.length === 0) {
                cards.forEach((card) => this.showPrice(card, null));
                return;
            }
            const code = codes[0].code || codes[0];
            await co2.setDiscountCode(code);

            for (const card of cards) {
                let variantTags = [];
                try { variantTags = JSON.parse(card.dataset.variantTags || '[]') || []; } catch (e) {}
                const price = Number(card.dataset.price);
                const result = await co2.itemDiscountFull(
                    Number(card.dataset.productId),
                    Number(card.dataset.variantId),
                    (card.dataset.productTags || '').split(',').filter(Boolean),
                    price,
                    1,
                    false,
                    false,
                    Array.isArray(variantTags) ? variantTags : []
                );
                this.showPrice(card, result && result.discPrice > 0 && result.discPrice < price ? result.discPrice : null, code);
            }
        } catch (error) {
            console.warn('[BundleOffer]', error);
        }
    }

    // discountedCents null restores the regular bundle price and badge text
    showPrice(card, discountedCents, code = '') {
        const format = (cents) => (cents / 100).toFixed(2).replace('.', ',');
        const priceCents = discountedCents === null ? Number(card.dataset.price) : discountedCents;
        const fullCents = Number(card.dataset.fullPrice);
        const percentEl = card.querySelector('[data-bundle-offer-percent]');
        const badgeTextEl = card.querySelector('[data-bundle-offer-badge-text]');

        card.querySelector('[data-bundle-offer-per-item]').textContent = format(Math.floor(priceCents / Number(card.dataset.items)));
        card.querySelector('[data-bundle-offer-total]').textContent = format(priceCents);
        badgeTextEl.textContent = discountedCents === null ? badgeTextEl.dataset.original : code.toUpperCase();

        if (discountedCents === null || !(fullCents > priceCents)) {
            percentEl.textContent = percentEl.dataset.original;
        } else {
            percentEl.textContent = `- ${Math.round(100 - priceCents * 100 / fullCents)}% `;
        }
    }
}

if (!customElements.get('bundle-offer')) {
    customElements.define('bundle-offer', BundleOffer);
}
